/**
 * Requirement Coverage Store (Specs/compliance-checklist-spec.md)
 *
 * One coverage fetch per bid powers the Compliance Matrix column, the
 * Evaluation Criteria column, the overview block, and the section-editor
 * brief chips. Link create/delete is optimistic (chip appears/disappears
 * immediately) with a silent re-fetch to pick up derived-status changes.
 *
 * AI mapping suggestions are ephemeral: pending chips live here, rejections
 * are session-only, and nothing links without an explicit accept.
 */

import { create } from 'zustand'
import {
  getCoverage,
  createCoverageLink,
  deleteCoverageLink,
  suggestCoverageMappings,
  type CoveragePayload,
  type CoverageRow,
  type CoverageLink,
  type CoverageMappingSuggestion,
  type CreateCoverageLinkInput,
} from '@/lib/api'

export interface SectionDisplay {
  artifactName: string
  sectionTitle: string
}

interface CoverageState {
  /** Bid the current state belongs to (stores are global). */
  bidId: string | null
  coverage: CoveragePayload | null
  isLoading: boolean
  error: string | null

  /** Pending AI mapping suggestions (ephemeral — accept creates links). */
  suggestions: CoverageMappingSuggestion[]
  isSuggesting: boolean
  /** Session-only rejected suggestion keys (`requirementId:artifactId:sectionKey`). */
  rejectedKeys: Set<string>

  load: (bidId: string, opts?: { force?: boolean }) => Promise<void>
  /** Create a link optimistically. `display` paints the chip before the re-fetch. */
  link: (bidId: string, input: CreateCoverageLinkInput, display: SectionDisplay) => Promise<void>
  unlink: (bidId: string, linkId: string) => Promise<void>
  suggest: (bidId: string) => Promise<void>
  acceptSuggestion: (bidId: string, suggestion: CoverageMappingSuggestion, display: SectionDisplay) => Promise<void>
  rejectSuggestion: (suggestion: CoverageMappingSuggestion) => void
  clearSuggestions: () => void
}

export function suggestionKey(s: CoverageMappingSuggestion): string {
  return `${s.requirementId}:${s.artifactId}:${s.sectionKey}`
}

function patchRow(rows: CoverageRow[], requirementId: string, patch: (row: CoverageRow) => CoverageRow): CoverageRow[] {
  return rows.map((row) => (row.requirementId === requirementId ? patch(row) : row))
}

export const useCoverageStore = create<CoverageState>((set, get) => ({
  bidId: null,
  coverage: null,
  isLoading: false,
  error: null,
  suggestions: [],
  isSuggesting: false,
  rejectedKeys: new Set<string>(),

  load: async (bidId, opts) => {
    const state = get()
    if (!opts?.force && state.bidId === bidId && (state.coverage || state.isLoading)) return
    if (state.bidId !== bidId) {
      set({ bidId, coverage: null, suggestions: [], rejectedKeys: new Set<string>(), error: null })
    }
    set({ isLoading: true, error: null })
    try {
      const coverage = await getCoverage(bidId)
      // Bid may have changed while in flight
      if (get().bidId === bidId) set({ coverage, isLoading: false })
    } catch (error) {
      if (get().bidId === bidId) {
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load coverage',
        })
      }
    }
  },

  link: async (bidId, input, display) => {
    const requirementId = input.complianceItemId ?? input.evaluationCriterionId
    if (!requirementId) return

    const { link } = await createCoverageLink(bidId, input)
    const chip: CoverageLink = {
      id: link.id,
      artifactId: input.artifactId,
      artifactName: display.artifactName,
      sectionKey: input.sectionKey,
      sectionTitle: display.sectionTitle,
      source: link.source,
    }

    set((state) => {
      if (state.bidId !== bidId || !state.coverage) return state
      const kind = input.complianceItemId ? 'compliance' : 'criteria'
      const apply = (row: CoverageRow): CoverageRow => ({
        ...row,
        status: row.status === 'unmapped' ? 'mapped' : row.status,
        links: row.links.some((l) => l.id === chip.id) ? row.links : [...row.links, chip],
      })
      return {
        coverage: {
          ...state.coverage,
          [kind]: patchRow(state.coverage[kind], requirementId, apply),
        },
      }
    })

    // Silent refresh: derived statuses + summaries need the server join
    void get().load(bidId, { force: true })
  },

  unlink: async (bidId, linkId) => {
    // Optimistic removal
    set((state) => {
      if (state.bidId !== bidId || !state.coverage) return state
      const strip = (rows: CoverageRow[]): CoverageRow[] =>
        rows.map((row) => {
          if (!row.links.some((l) => l.id === linkId)) return row
          const links = row.links.filter((l) => l.id !== linkId)
          return { ...row, links, status: links.length === 0 ? 'unmapped' : row.status }
        })
      return {
        coverage: {
          ...state.coverage,
          compliance: strip(state.coverage.compliance),
          criteria: strip(state.coverage.criteria),
        },
      }
    })

    try {
      await deleteCoverageLink(bidId, linkId)
    } finally {
      void get().load(bidId, { force: true })
    }
  },

  suggest: async (bidId) => {
    set({ isSuggesting: true, error: null })
    try {
      const { suggestions } = await suggestCoverageMappings(bidId)
      const state = get()
      if (state.bidId !== bidId) return
      // Drop session-rejected pairs and pairs already linked
      const linkedKeys = new Set<string>()
      for (const row of [...(state.coverage?.compliance ?? []), ...(state.coverage?.criteria ?? [])]) {
        for (const link of row.links) {
          linkedKeys.add(`${row.requirementId}:${link.artifactId}:${link.sectionKey}`)
        }
      }
      set({
        suggestions: suggestions.filter(
          (s) => !state.rejectedKeys.has(suggestionKey(s)) && !linkedKeys.has(suggestionKey(s))
        ),
        isSuggesting: false,
      })
    } catch (error) {
      set({
        isSuggesting: false,
        error: error instanceof Error ? error.message : 'Failed to suggest mappings',
      })
    }
  },

  acceptSuggestion: async (bidId, suggestion, display) => {
    const target = { artifactId: suggestion.artifactId, sectionKey: suggestion.sectionKey, source: 'ai' as const }
    const input: CreateCoverageLinkInput =
      suggestion.requirementKind === 'compliance'
        ? { complianceItemId: suggestion.requirementId, ...target }
        : { evaluationCriterionId: suggestion.requirementId, ...target }
    set((state) => ({
      suggestions: state.suggestions.filter((s) => suggestionKey(s) !== suggestionKey(suggestion)),
    }))
    await get().link(bidId, input, display)
  },

  rejectSuggestion: (suggestion) => {
    set((state) => {
      const rejectedKeys = new Set(state.rejectedKeys)
      rejectedKeys.add(suggestionKey(suggestion))
      return {
        rejectedKeys,
        suggestions: state.suggestions.filter((s) => suggestionKey(s) !== suggestionKey(suggestion)),
      }
    })
  },

  clearSuggestions: () => set({ suggestions: [] }),
}))
